import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'
import { ElementType } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import {
    PT_GROUP_ACTIVATE_MESSAGE,
    PT_GROUP_DEACTIVATE_MESSAGE,
    PT_PARA_ACTIVATE_MESSAGE,
    PT_PARA_DEACTIVATE_MESSAGE,
} from 'pages/components/constants'
import { isOpenKeyParagraph } from '../components/ContentEditor/contentUtils'
import { ContentItemViewNodesType, DataNode } from '../types'
import { syncVisibilityBetweenBODAndManufacturers } from './bodUtils'

/**
 * Retrieves parent article node of a given content node.
 *
 */
export const getParentArticleNode = (
    currentNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType> => {
    if (currentNode?.textType === 'Article') {
        return currentNode
    } else {
        const parentNode = contentNodes?.find((item) =>
            currentNode?.dtvContentItemParentId
                ? item?.id === currentNode?.dtvContentItemParentId
                : item?.dtvItemId === currentNode?.dtvParentId
        )
        if (parentNode) {
            return getParentArticleNode(parentNode, contentNodes)
        } else return {}
    }
}

/**
 * Determines if dtvParentId should be skipped when finding children.
 * Skip dtvParentId for nested paragraphs to prevent
 * incorrect sibling paragraphs from being treated as children.
 *
 * @param node - The node to check
 * @returns True if dtvParentId should be skipped, false otherwise
 */
const shouldSkipDtvParentId = (
    node: Partial<ContentItemViewNodesType>
): boolean => {
    // Only skip for paragraphs that have an explicit parent (nested paragraphs)
    // Top-level paragraphs (dtvContentItemParentId is null/undefined/empty) should use dtvParentId
    return (
        node?.textType === 'Paragraph' &&
        node?.dtvContentItemParentId != null &&
        node?.dtvContentItemParentId !== ''
    )
}

/**
 * Retrieves child nodes of a given content node based on the hierarchical level.
 *
 * @param level - The level in the hierarchy (0 for direct children, 1 or more for deeper levels).
 * @param hoveredNode - The node currently being hovered over; used as a reference for matching children.
 * @param contentNodes - The complete list of content nodes to filter from.
 * @param parentNodes - (Optional) The list of parent nodes to match children against when level > 0.
 * @returns A filtered list of nodes that are children of the hovered or parent nodes.
 */
const getChildContentItemViewNodes = (
    level: number,
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    parentNodes?: Partial<ContentItemViewNodesType>[]
) => {
    if (level === 0) {
        // Match TreeDataMapper.findChildren() logic for consistency
        const children = contentNodes?.filter((item) => {
            // Check for BOD child relationship
            const isBodChildNode = item?.refBodItemId === hoveredNode?.dtvItemId

            // Check for content item parent relationship
            const isChildNode =
                (!item?.refBodItemId || item?.refBodItemId === '') &&
                item?.dtvContentItemParentId === hoveredNode?.id

            // Check for direct DTV parent relationship
            const isDirectChildNode =
                item?.dtvParentId === hoveredNode?.dtvItemId &&
                !item?.dtvContentItemParentId &&
                (!item?.refBodItemId || item?.refBodItemId === '') &&
                !shouldSkipDtvParentId(hoveredNode)

            return isChildNode || isDirectChildNode || isBodChildNode
        })

        return children
    } else {
        // For levels > 0, find children of the parent nodes from the previous level
        return contentNodes?.filter((item) => {
            // Check if this item is a child of any parent node from the previous level
            return parentNodes?.some((parentNode) => {
                // Check for BOD child relationship
                const isBodChildNode =
                    item?.refBodItemId === parentNode?.dtvItemId

                // Check for content item parent relationship
                const isChildNode =
                    (!item?.refBodItemId || item?.refBodItemId === '') &&
                    item?.dtvContentItemParentId === parentNode?.id

                // Check for direct DTV parent relationship
                const isDirectChildNode =
                    item?.dtvParentId === parentNode?.dtvItemId &&
                    !item?.dtvContentItemParentId &&
                    (!item?.refBodItemId || item?.refBodItemId === '') &&
                    !shouldSkipDtvParentId(parentNode)

                return isChildNode || isDirectChildNode || isBodChildNode
            })
        })
    }
}

/**
 * Retrieves all child nodes of a given content node.
 *
 */
export const getChildElements = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
) => {
    const levelOneChild = getChildContentItemViewNodes(
        0,
        hoveredNode,
        contentNodes
    )
    const levelTwoChild = getChildContentItemViewNodes(
        1,
        hoveredNode,
        contentNodes,
        levelOneChild
    )
    const levelThreeChild = getChildContentItemViewNodes(
        2,
        hoveredNode,
        contentNodes,
        levelTwoChild
    )
    // Add level 4 for deeply nested children
    const levelFourChild = getChildContentItemViewNodes(
        3,
        hoveredNode,
        contentNodes,
        levelThreeChild
    )
    // Add level 5 for even deeper nesting
    const levelFiveChild = getChildContentItemViewNodes(
        4,
        hoveredNode,
        contentNodes,
        levelFourChild
    )

    return [
        ...levelOneChild,
        ...levelTwoChild,
        ...levelThreeChild,
        ...levelFourChild,
        ...levelFiveChild,
    ]
}
/**
 * Retrieves all parent nodes upto top level
 *
 */

export const findAncestors = (
    contentNodes: Partial<ContentItemViewNodesType>[],
    currentNode: Partial<ContentItemViewNodesType>
) => {
    const contentItemIdMap = new Map<
        string,
        Partial<ContentItemViewNodesType>
    >()
    const dtvItemIdMap = new Map<string, Partial<ContentItemViewNodesType>>()

    contentNodes.forEach((node) => {
        if (node.id) {
            contentItemIdMap.set(node.id, node)
        }
        if (node.dtvItemId) {
            dtvItemIdMap.set(node.dtvItemId, node)
        }
    })

    let ancestors = new Array()
    let localNode = currentNode

    while (localNode) {
        const parentViaContentItemId = localNode.dtvContentItemParentId
            ? contentItemIdMap.get(localNode.dtvContentItemParentId)
            : undefined
        const parentViaDtvItemId = localNode.dtvParentId
            ? dtvItemIdMap.get(localNode.dtvParentId)
            : undefined

        const parent = parentViaContentItemId || parentViaDtvItemId

        if (!parent) break
        if (ancestors) {
            ancestors?.push(parent)
        }
        localNode = parent
    }

    return ancestors
}

/**
 * check if all content element nodes inside parent article node are hidden
 *
 */
export const areAllChildrenInsideArticleHidden = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    if (!isHidden) return false
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const childNodes = getChildElements(articleNode, contentNodes)
    const activeElementNodes = childNodes?.filter(
        (item) =>
            !item?.isHidden &&
            !item?.isDeactivated &&
            item?.id !== hoveredNode?.id
    )

    return activeElementNodes?.length === 0
}

/**
 * Determines if a node's explicit state should be preserved during visibility changes.
 */
const shouldPreserveNodeExplicitState = (
    item: Partial<ContentItemViewNodesType>,
    hoveredNode: Partial<ContentItemViewNodesType>,
    nodeIdsToUpdate: Set<string>,
    isParentAncestor: boolean,
    isHidden: boolean
): boolean => {
    const hasOppositeExplicitState = (item.isHidden ?? false) === !isHidden

    // Don't preserve if it doesn't have an opposite state
    if (!hasOppositeExplicitState) {
        return false
    }

    // Never preserve parent ancestors when activating
    if (isParentAncestor) {
        return false
    }

    // Check if this item is in the nodeIdsToUpdate set (is a child of the hovered node)
    const isChildOfHoveredNode = item?.id ? nodeIdsToUpdate.has(item.id) : false

    // Don't preserve children of the hovered node - they should be updated with their parent
    if (isChildOfHoveredNode) {
        return false
    }

    // Check if this is a mislinked node (has same parent but linked as child)
    const isMislinkedNode =
        item?.dtvContentItemParentId === hoveredNode?.id &&
        item?.dtvParentId === hoveredNode?.dtvParentId

    // Only preserve:
    // - Mislinked nodes (incorrectly linked siblings)
    // - True siblings with explicit opposite state (not children of hovered node)
    // - Unrelated nodes with explicit opposite state
    return isMislinkedNode || !isChildOfHoveredNode
}

/**
 * Finds all related child nodes up to three levels deep from the hovered node
 * and updates their visibility based on the `isHidden` flag.
 *
 * Note: This function updates all child nodes of the hovered node. If nodes appear
 * as siblings in the UI but have a parent-child relationship via dtvContentItemParentId,
 * they will all be updated together. This is the expected behavior for maintaining
 * consistency in the tree structure.
 *
 * @param hoveredNode - The content node that is the current point of reference.
 * @param contentNodes - The complete list of content nodes.
 * @param isHidden - Flag indicating whether the matched nodes should be hidden or shown.
 * @returns A new array of content nodes with `isHidden` updated for the matched nodes.
 */

// Direct children by intra-DTV (dtvContentItemParentId) or cross-DTV (dtvParentId) — getChildElements misses some cross-DTV items.
const getDirectChildren = (
    parent: Partial<ContentItemViewNodesType>,
    nodes: Partial<ContentItemViewNodesType>[]
) =>
    nodes.filter(
        (item) =>
            (item?.dtvContentItemParentId &&
                item.dtvContentItemParentId === parent.id) ||
            (!item?.dtvContentItemParentId &&
                item?.dtvParentId === parent.dtvItemId)
    )

export const getAllChildrenFromHoveredNode = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    //check for manufacturer/BOD paragraph
    if (hoveredNode?.isBOD || hoveredNode?.isManufacturers) {
        let syncedNodes = syncVisibilityBetweenBODAndManufacturers(
            hoveredNode,
            contentNodes,
            isHidden
        )
        // syncVisibilityBetweenBODAndManufacturers only updates paragraph-level nodes
        // (the BOD/Manufacturer paragraph itself and its counterpart). It does not touch
        // content items nested inside those paragraphs (e.g. BOD pills). Apply the same
        // isHidden/isDeactivated state to those children here.
        const childElements = getChildElements(hoveredNode, syncedNodes)
        if (childElements.length > 0) {
            const childIds = new Set(
                childElements
                    .map((c) => c.id)
                    .filter((id): id is string => !!id)
            )
            syncedNodes = syncedNodes.map((node) => {
                if (!node.id || !childIds.has(node.id)) return node
                return { ...node, isHidden, isDeactivated: isHidden }
            })
        }
        return syncedNodes
    }
    const nodesToUpdate = getChildElements(hoveredNode, contentNodes)
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const parentAncestorNodes = isHidden
        ? []
        : findAncestors(contentNodes, hoveredNode)

    // Pre-compute node IDs for efficient lookup (performance optimization)
    const nodeIdsToUpdate = new Set<string>(
        nodesToUpdate
            .map((node) => node?.id)
            .filter((id): id is string => id !== undefined && id !== null)
    )

    let updatedNodes = contentNodes.map((item) => {
        const shouldUpdate =
            parentAncestorNodes?.some(
                (ancestor) => ancestor?.id === item?.id
            ) ||
            (item?.id && nodeIdsToUpdate.has(item.id)) ||
            item?.id === hoveredNode?.id ||
            (!isHidden && item?.id === articleNode?.id) ||
            (!isHidden &&
                (item?.dtvItemId === hoveredNode?.dtvParentId ||
                    item?.id === hoveredNode.dtvContentItemParentId))

        if (shouldUpdate) {
            // Always update the hovered node itself
            if (item?.id === hoveredNode?.id) {
                // Sync isDeactivated with isHidden for OM workspace optimistic updates
                const updates = { isHidden, isDeactivated: isHidden }
                if (!isHidden && item?.isDeactivated) {
                    return { ...item, ...updates, isDeactivated: false }
                }
                return { ...item, ...updates }
            }

            // When activating, parent ancestors must be activated
            const isParentAncestor =
                !isHidden &&
                parentAncestorNodes?.some(
                    (ancestor) => ancestor?.id === item?.id
                )

            const shouldPreserveExplicitState = shouldPreserveNodeExplicitState(
                item,
                hoveredNode,
                nodeIdsToUpdate,
                isParentAncestor,
                isHidden
            )

            if (shouldPreserveExplicitState) {
                return item
            }

            // When activating, keep non-selected sustainability children hidden.
            // Only children matching the project's sustainability parameters
            // (isSelectedSustainabilityContentItem) should be activated.
            // Exceptions — skip this filter when:
            //   - item is a parent ancestor (clicking a child must activate its parent)
            //   - hoveredNode itself is a sustainability item (clicking a sustainability
            //     topic activates ALL its children, selected or not)
            if (
                !isHidden &&
                item?.isSustainabilityContentItem &&
                !item?.isSelectedSustainabilityContentItem &&
                !isParentAncestor &&
                !hoveredNode?.isSustainabilityContentItem
            ) {
                return item
            }

            // Sync isDeactivated with isHidden for OM workspace optimistic updates
            const updates = { isHidden, isDeactivated: isHidden }
            if (!isHidden && item?.isDeactivated) {
                return { ...item, ...updates, isDeactivated: false }
            }
            return { ...item, ...updates }
        }

        return item
    })
    // ensure BOD/Manufacturer selection options also reflect visibility state
    const specialNodes = nodesToUpdate.filter(
        (node) => node?.isBOD || node?.isManufacturers
    )
    for (const bodNode of specialNodes) {
        updatedNodes = syncVisibilityBetweenBODAndManufacturers(
            bodNode,
            updatedNodes,
            isHidden
        )
    }
    // Bug 2700071: cascade-deactivate parent paragraph when all subparagraphs are deactivated.
    if (isHidden) {
        const parentParagraph = updatedNodes?.find((item) =>
            hoveredNode?.dtvContentItemParentId
                ? item?.id === hoveredNode?.dtvContentItemParentId
                : item?.dtvItemId === hoveredNode?.dtvParentId
        )
        if (
            parentParagraph &&
            parentParagraph?.textType === 'Paragraph' &&
            parentParagraph?.id !== hoveredNode?.id
        ) {
            const isInactive = (n: Partial<ContentItemViewNodesType>) =>
                !!n?.isHidden || !!n?.isDeactivated
            const directChildren = getDirectChildren(
                parentParagraph,
                updatedNodes
            )
            // Grandchildren are cross-DTV — OR rule (isInactive), not dual-flag AND.
            const seen = new Set<string>()
            const directChildDescendants = directChildren
                .flatMap((child) => getDirectChildren(child, updatedNodes))
                .filter((item) => {
                    if (!item?.id || seen.has(item.id)) return false
                    seen.add(item.id)
                    return true
                })
            const hasCrossDtvDirectChildren = directChildren.some(
                (child) => child?.dtvItemId !== parentParagraph?.dtvItemId
            )
            // Intra-DTV: both flags required — spec-template items can be isHidden without isDeactivated.
            const areDirectChildrenDeactivated = hasCrossDtvDirectChildren
                ? directChildren.every(isInactive)
                : directChildren.every(
                      (n) => !!n?.isHidden && !!n?.isDeactivated
                  )
            const allChildrenDeactivated =
                directChildren.length > 0 &&
                areDirectChildrenDeactivated &&
                (directChildDescendants.length === 0 ||
                    directChildDescendants.every(isInactive))
            if (
                allChildrenDeactivated &&
                isOpenKeyParagraph(parentParagraph?.elements ?? [])
            ) {
                updatedNodes = updatedNodes.map((item) =>
                    item?.id === parentParagraph?.id
                        ? { ...item, isHidden: true, isDeactivated: true }
                        : item
                )
                // parentParagraph hidden — also cascade article if all its direct children are now deactivated.
                if (articleNode?.id && articleNode.id !== parentParagraph?.id) {
                    // Dual-flag (isHidden && isDeactivated) — not OR — so spec-template items
                    // (isHidden:true, isDeactivated:false) count as active and block the cascade.
                    const directArticleChildren = getDirectChildren(
                        articleNode,
                        updatedNodes
                    )
                    const hasActiveDirectArticleChild = directArticleChildren.some(
                        (item) => !(!!item?.isHidden && !!item?.isDeactivated)
                    )
                    if (
                        directArticleChildren.length > 0 &&
                        !hasActiveDirectArticleChild
                    ) {
                        updatedNodes = updatedNodes.map((item) =>
                            item?.id === articleNode?.id
                                ? {
                                      ...item,
                                      isHidden: true,
                                      isDeactivated: true,
                                  }
                                : item
                        )
                    }
                }
            }
        }
    }

    // Cascade article only when hoveredNode is a direct child — prevents false-positives from the open-key paragraph cascade above.
    const isDirectArticleChild =
        !!articleNode?.id &&
        (hoveredNode?.dtvContentItemParentId === articleNode.id ||
            (!hoveredNode?.dtvContentItemParentId &&
                hoveredNode?.dtvParentId === articleNode?.dtvItemId))
    const shouldHideArticle =
        isHidden &&
        isDirectArticleChild &&
        areAllChildrenInsideArticleHidden(hoveredNode, updatedNodes, isHidden)
    if (shouldHideArticle && articleNode?.id !== hoveredNode?.id) {
        updatedNodes = updatedNodes.map((item) =>
            item?.id === articleNode?.id
                ? { ...item, isHidden: true, isDeactivated: true }
                : item
        )
    }

    return updatedNodes
}

/**
 * Checks if a node's parent product type node is hidden.
 * This is used to determine if activation/deactivation should be disabled
 * for paragraphs whose parent product type is hidden.
 *
 * @param node - The node to check (typically a paragraph within a product type)
 * @param contentItemNodes - Array of all content item nodes in the tree
 * @returns true if the parent product type node is hidden or if the node is not product type content
 *
 * @remarks
 * - Returns true for non-product-type content as a safety measure to prevent activation
 * - Handles both dtvParentId and dtvContentItemParentId relationships
 * - Recursively traverses up the parent chain to find the parent product type at any level
 * - Returns false if parent is not found (assumes parent is visible)
 */
export const isParentProductTypeNodeHidden = (
    node: Partial<DataNode>,
    contentItemNodes: Partial<ContentItemViewNodesType>[]
): boolean => {
    // Safety check: if not product type content, enable activation
    if (!node?.isProductTypeContent) return false

    // Defensive check: if no contentItemNodes provided, assume parent is visible
    if (!contentItemNodes || contentItemNodes.length === 0) return false

    // If no contentItemView, cannot determine parent
    if (!node?.contentItemView) return false

    // Use findAncestors to get all ancestors up the tree
    const ancestors = findAncestors(contentItemNodes, node.contentItemView)

    // Find the first ancestor that is a product type
    const parentProductType = ancestors.find(
        (ancestor) => ancestor?.isProductType
    )

    // If parent product type not found, assume it's visible (return false)
    // If found, return its isHidden status (default to false if undefined)
    return parentProductType?.isHidden ?? false
}

/**
 * Extracts the plain-text display name of a PT Group or PT Para node.
 * Looks up the node in the store's contentItemViewNodes for the freshest
 * element text, then falls back to the DataNode's own contentItemNodes.
 * Strips CKEditor HTML tags before returning.
 */
export const getPtNodeDisplayName = (
    hoveredNode: DataNode,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[] | null | undefined
): string => {
    const contentNode = contentItemViewNodes?.find(
        (item) => item?.id === (hoveredNode.key as string)
    )
    const rawName =
        contentNode?.elements?.[0]?.text ??
        hoveredNode.contentItemNodes?.[0]?.text ??
        ''
    const stripped = rawName.replace(/<[^>]*>/g, '').trim()
    const colonIdx = stripped.indexOf(':')
    return colonIdx !== -1 ? stripped.slice(0, colonIdx).trim() : stripped
}

/**
 * Returns the dialog message for PT Group / PT Para visibility restriction dialogs.
 */
export const getPtDeactivationDialogMessage = (info: {
    isProductGroup: boolean
    isArticleLevel: boolean
    isHidden: boolean
    nodeName: string
}): string => {
    if (info.isProductGroup) {
        return info.isHidden
            ? PT_GROUP_DEACTIVATE_MESSAGE(info.isArticleLevel)
            : PT_GROUP_ACTIVATE_MESSAGE(info.isArticleLevel)
    }

    return info.isHidden
        ? PT_PARA_DEACTIVATE_MESSAGE(info.nodeName)
        : PT_PARA_ACTIVATE_MESSAGE(info.nodeName)
}

/**
 * Returns Project element node id for deactivation mutations
 *
 * @param {ProjectElementNode} selectedProjectElementNode - The current selected project element node
 * @param {ProjectElementNodeWithDrawingDesignation[]} familyAddedProductTypes - List of product types added to the family
 * @param {DataNode} node - The node being toggled
 * @returns {string | undefined} The target project element node ID for mutateDeactivationAsync
 */
export const getDeactivationTargetElementNodeId = (
    selectedProjectElementNode: ProjectElementNode | null,
    familyAddedProductTypes:
        | ProjectElementNodeWithDrawingDesignation[]
        | undefined,
    node: DataNode
): string | undefined => {
    const isProductFamily =
        selectedProjectElementNode?.elementType === ElementType.PRODUCTFAMILY
    const isManufacturerOrBOD = node.isBOD || node.isManufacturers

    // For MFR and BOD paragraphs, use the Product Type Id that owns this paragraph
    if (isProductFamily && isManufacturerOrBOD) {
        const addedPT:
            | ProjectElementNodeWithDrawingDesignation
            | undefined = familyAddedProductTypes?.find(
            (pt) => pt.baseElementId === node.contentItemView?.dtvParentId
        )

        if (addedPT) {
            return addedPT.id
        }
    }

    return selectedProjectElementNode?.id
}
